import { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"
import { AlertCircle } from "lucide-react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import { StepImporting } from "./steps/step-importing"
import { StepPickFile } from "./steps/step-pick-file"
import { StepPreview } from "./steps/step-preview"
import { useImportNoCallCSV } from "./use-import-no-call-csv"

type Props = {
  close: (result: boolean | null) => void
}

export function DialogUploadNoCallCSV({ close }: Props) {
  const { t } = useTranslation()
  const { getProgress, reset, selectFile, startImport, state } =
    useImportNoCallCSV()

  const isPending =
    state.phase === "parsing" ||
    state.phase === "checking" ||
    state.phase === "importing"
  const isIdlePhase = state.phase === "idle" || state.phase === "parsing"
  const isImportingPhase =
    state.phase === "importing" || state.phase === "completed"

  return (
    <DialogWrapper isPending={isPending}>
      <DialogBody className="overflow-hidden">
        {isIdlePhase && (
          <StepPickFile isDisabled={isPending} onFileSelect={selectFile} />
        )}

        {state.phase === "checking" && state.checkProgress && (
          <StepChecking progress={state.checkProgress} />
        )}

        {state.phase === "parsing_error" && (
          <StepParseError parseErrors={state.parseErrors} onReset={reset} />
        )}

        {state.phase === "preview" && (
          <StepPreview
            rows={state.rows}
            onReset={reset}
            onStartImport={startImport}
          />
        )}

        {isImportingPhase && (
          <StepImporting
            isCompleted={state.phase === "completed"}
            rows={state.rows}
            progress={getProgress()}
            onClose={() => close(true)}
            onReset={reset}
          />
        )}
      </DialogBody>

      {isIdlePhase && (
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>
            {t("global.button.cancel")}
          </Button>
        </DialogFooter>
      )}
    </DialogWrapper>
  )
}

function StepChecking({
  progress,
}: {
  progress: { completed: number; total: number }
}) {
  const { t } = useTranslation()
  const percentage = progress.total
    ? Math.round((progress.completed / progress.total) * 100)
    : 0

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between text-sm">
        <span>
          {t("noCallList.importCSV.checking.progress", {
            completed: progress.completed,
            total: progress.total,
          })}
        </span>
        <span>{percentage}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-200">
        <div
          className="
            h-full rounded-full bg-primary-300 transition-all duration-300
          "
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}

function StepParseError({
  onReset,
  parseErrors,
}: {
  onReset: () => void
  parseErrors: string[]
}) {
  const { t } = useTranslation()

  const message = parseErrors
    .map((key) =>
      t(`noCallList.importCSV.warnings.${key}`, {
        defaultValue: t("noCallList.importCSV.warnings.INTERNAL_ERROR"),
      }),
    )
    .join("; ")

  return (
    <div className="flex flex-col gap-4">
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertDescription>{message}</AlertDescription>
      </Alert>
      <Button variant="outline" onClick={onReset}>
        {t("global.button.goBack")}
      </Button>
    </div>
  )
}

function DialogWrapper({
  children,
  isPending,
}: PropsWithChildren<{ isPending?: boolean }>) {
  const { t } = useTranslation()

  return (
    <DialogContent className="max-w-4xl" isPending={isPending}>
      <DialogHeader>
        <DialogTitle>{t("noCallList.importCSV.action")}</DialogTitle>
      </DialogHeader>
      {children}
    </DialogContent>
  )
}
